import { useRef, useState, useEffect, useCallback } from "react";
import { Calendar, Play, Trash2, Loader, RefreshCw, Filter } from "lucide-react";
import axiosInstance from "../../API/interceptor";

// Convert UTC server time to browser's local timezone for display
const fmtDate = (v) => {
  if (!v) return "—";
  try {
    const dt = new Date(v);
    if (isNaN(dt.getTime())) return "—";
    const dd   = String(dt.getDate()).padStart(2, "0");
    const mm   = String(dt.getMonth() + 1).padStart(2, "0");
    const h24  = dt.getHours();
    const h12  = h24 % 12 || 12;
    const ampm = h24 >= 12 ? "PM" : "AM";
    const min  = String(dt.getMinutes()).padStart(2, "0");
    return `${dd}-${mm}-${dt.getFullYear()} ${String(h12).padStart(2,"0")}:${min} ${ampm}`;
  } catch { return "—"; }
};


const cols = [
  { key: "sl",        label: "#",                    w: 60  },
  { key: "taskName",  label: "TASK NAME",             w: 220 },
  { key: "template",  label: "TEMPLATE NAME",         w: 200 },
  { key: "workspace", label: "WORKSPACE",             w: 180 },
  { key: "savedAt",   label: "DATE OF CREATE DRAFT",  w: 190 },
  { key: "action",    label: "ACTION",                w: 110 },
];

export default function DraftView({ onResume, workspaces = [] }) {
  const [drafts,    setDrafts]    = useState([]);
  const [loading,   setLoading]   = useState(false);
  const [deleting,  setDeleting]  = useState(null);
  const [resuming,  setResuming]  = useState(null);
  const [workspace, setWorkspace] = useState("");
  const scrollRef = useRef();
  const drag = useRef({ active: false, startX: 0, scrollLeft: 0 });

  // ── Load drafts — same as HTML loadDraftsForWorkspace ──
  const loadDrafts = useCallback(async (productCode = "") => {
    setLoading(true);
    try {
      const url = productCode
        ? `/task_assignment/get_all_drafts/${productCode}`
        : `/task_assignment/get_all_drafts`;
      const res  = await axiosInstance.get(url);
      const data = Array.isArray(res.data?.drafts) ? res.data.drafts : [];
      setDrafts(data);
    } catch {
      setDrafts([]);
    } finally {
      setLoading(false);
    }
  }, []);

  // Auto-load on mount — same as HTML #drafts-tab shown.bs.tab
  useEffect(() => { loadDrafts(""); }, []);

  // ── Workspace filter change ──
  const handleWorkspaceChange = (e) => {
    const val = e.target.value;
    setWorkspace(val);
    loadDrafts(val);
  };

  // ── Resume — same as HTML resume-draft-from-table click ──
  // HTML flow: resume button → get_template_draft → restore all fields → open modal
  const handleResume = async (draft) => {
    const draftId = draft.draft_id || draft._id;
    setResuming(draftId);
    try {
      // ✅ Same as HTML: get_template_draft call
      const res = await axiosInstance.get(
        `/task_assignment/get_template_draft/${draftId}`
      );
      const fullDraft = res.data?.draft || res.data;
      if (!fullDraft) { alert("Draft not found"); return; }

      // ✅ Same as HTML resumeDraft — pass everything to CreateTaskDrawer
      // CreateTaskDrawer's resumeDraftData useEffect will restore all fields
      if (onResume) {
        onResume({
          _id:             draftId,
          // workspace + template info
          product_code:    fullDraft.product_code  || draft.product_code,
          template_id:     fullDraft.template_id   || draft.template_id,
          template_name:   fullDraft.template_name || draft.template_name,
          // ✅ main_task_data — HTML collectMainTaskData fields:
          // task_type, detailed_description, priority, task_stage,
          // functional_categories, start_date, completion_sla,
          // created_by, created_by_name,
          // assign_to_user_names [{id,text}], reviewer_user_names, informer_user_names,
          // completion_approval, task_comment,
          // repeat, repeat_interval, repeat_interval_type, end_date
          main_task_data:  fullDraft.main_task_data  || {},
          // ✅ subtasks_data — same as HTML templateSubtasksData fields:
          // name, detailed_description, priority, start_date, completion_sla,
          // accountable, accountable_name, responsible, responsible_name,
          // consulted_resolved, informer_resolved, completionApproval, comment
          subtasks_data:   fullDraft.subtasks_data   || [],
          // ✅ already uploaded files
          main_task_files: fullDraft.main_task_files || [],
          // ✅ saved step — open drawer at this step
          current_step:    fullDraft.current_step    || 0,
        });
      }
    } catch { alert("Failed to load draft. Please try again."); }
    finally { setResuming(null); }
  };

  // ── Delete — same as HTML delete-draft-from-table click ──
  const handleDelete = async (draftId) => {
    if (!window.confirm("This will permanently delete this draft. Are you sure?")) return;
    setDeleting(draftId);
    try {
      await axiosInstance.post(
        `/task_assignment/delete_template_draft/${draftId}`
      );
      setDrafts(prev => prev.filter(d => (d.draft_id || d._id) !== draftId));
    } catch { alert("Failed to delete draft"); }
    finally { setDeleting(null); }
  };

  // Drag to scroll
  const onMouseDown = e => {
    drag.current = { active: true, startX: e.pageX - scrollRef.current.offsetLeft, scrollLeft: scrollRef.current.scrollLeft };
    scrollRef.current.style.cursor      = "grabbing";
    scrollRef.current.style.userSelect  = "none";
  };
  const onMouseUp = () => {
    drag.current.active = false;
    if (scrollRef.current) { scrollRef.current.style.cursor = "grab"; scrollRef.current.style.userSelect = ""; }
  };
  const onMouseMove = e => {
    if (!drag.current.active) return;
    e.preventDefault();
    const x = e.pageX - scrollRef.current.offsetLeft;
    scrollRef.current.scrollLeft = drag.current.scrollLeft - (x - drag.current.startX);
  };

  return (
    <div className="rounded-xl border border-gray-100 shadow-sm my-5 mx-5">

      {/* ── Header — workspace filter + refresh (same as HTML) ── */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100 flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <Filter size={13} className="text-slate-400"/>
          <span className="text-xs font-bold text-slate-500 uppercase tracking-wider">Saved Drafts</span>
        </div>
        <div className="flex items-center gap-2">
          {/* Workspace filter — same as HTML drafts_workspace_dropdown */}
          {workspaces.length > 0 && (
            <select value={workspace} onChange={handleWorkspaceChange}
              className="text-xs border border-gray-200 rounded-lg px-2 py-1.5 text-slate-600 bg-white outline-none cursor-pointer">
              <option value="">All Workspaces</option>
              {workspaces.map(w => (
                <option key={w.code} value={w.code}>{w.name}</option>
              ))}
            </select>
          )}
          {/* Refresh — same as HTML drafts-refresh-btn */}
          <button onClick={() => loadDrafts(workspace)} disabled={loading}
            className="w-7 h-7 rounded-lg border border-gray-200 bg-white flex items-center justify-center cursor-pointer hover:bg-gray-50 transition-colors">
            {loading
              ? <Loader size={12} className="text-violet-600 animate-spin"/>
              : <RefreshCw size={12} className="text-slate-400"/>}
          </button>
        </div>
      </div>

      {/* ── Table ── */}
      <div ref={scrollRef} className="overflow-x-auto cursor-grab"
        onMouseDown={onMouseDown} onMouseUp={onMouseUp}
        onMouseLeave={onMouseUp}  onMouseMove={onMouseMove}>
        <table className="w-full border-separate border-spacing-0 text-sm">
          <colgroup>{cols.map(c => <col key={c.key} style={{ minWidth: c.w, width: c.w }}/>)}</colgroup>
          <thead>
            <tr>
              {cols.map(c => (
                <th key={c.key}
                  className="px-3.5 py-3 text-left text-[10.5px] font-bold text-slate-400 tracking-widest bg-slate-50 border-b-2 border-gray-100 whitespace-nowrap">
                  {c.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={cols.length} className="py-14 text-center">
                  <Loader size={20} className="text-violet-500 animate-spin mx-auto"/>
                  <p className="text-xs text-slate-400 mt-2">Loading drafts...</p>
                </td>
              </tr>
            ) : drafts.length === 0 ? (
              <tr>
                <td colSpan={cols.length} className="py-14 text-center text-slate-300 text-sm">
                  No drafts found
                </td>
              </tr>
            ) : drafts.map((d, i) => {
              const rowBg  = i % 2 === 0 ? "#fff" : "#FAFBFE";
              const td     = "px-3.5 py-3 border-b border-gray-50";
              const draftId = d.draft_id || d._id;
              // Task name — same as HTML main_task_data?.task_type
              const taskName = d.main_task_data?.task_type || "—";
              // Workspace name
              const wsName = d.workspace_name || d.product_code || "—";
              // Date — same as HTML saved_at_utc
              const dateStr = fmtDate(d.saved_at_utc || d.saved_at);

              return (
                <tr key={draftId} className="group">
                  <td className={td} style={{ background: rowBg }}>
                    <span className="font-mono text-[11px] bg-indigo-50 text-indigo-700 px-1.5 py-0.5 rounded-md font-semibold">
                      #{String(i + 1).padStart(2, "0")}
                    </span>
                  </td>
                  <td className={td} style={{ background: rowBg }}>
                    <span className="text-xs font-semibold text-gray-700">{taskName}</span>
                  </td>
                  <td className={td} style={{ background: rowBg }}>
                    <span className="text-[11px] bg-violet-50 text-violet-700 px-2 py-0.5 rounded-md border border-violet-100">
                      {d.template_name || "—"}
                    </span>
                  </td>
                  <td className={td} style={{ background: rowBg }}>
                    <span className="text-[11px] bg-slate-100 text-slate-600 px-2 py-0.5 rounded-md">
                      {wsName}
                    </span>
                  </td>
                  <td className={td} style={{ background: rowBg }}>
                    <span className="text-[11px] text-slate-500 flex items-center gap-1">
                      <Calendar size={11} color="#94A3B8"/>
                      {dateStr}
                    </span>
                  </td>
                  <td className={td} style={{ background: rowBg }}>
                    <div className="flex items-center gap-2">
                      {/* Resume — same as HTML resume-draft-from-table */}
                      <button
                        onClick={() => handleResume(d)}
                        disabled={resuming === draftId}
                        title="Resume Draft"
                        className="w-7 h-7 rounded-lg border border-emerald-100 bg-emerald-50 text-emerald-600 flex items-center justify-center hover:bg-emerald-100 transition-colors cursor-pointer disabled:opacity-50">
                        {resuming === draftId
                          ? <Loader size={12} className="animate-spin"/>
                          : <Play size={12} fill="currentColor"/>}
                      </button>
                      {/* Delete — same as HTML delete-draft-from-table */}
                      <button
                        onClick={() => handleDelete(draftId)}
                        disabled={deleting === draftId}
                        title="Delete Draft"
                        className="w-7 h-7 rounded-lg border border-red-100 bg-red-50 text-red-500 flex items-center justify-center hover:bg-red-100 transition-colors cursor-pointer disabled:opacity-50">
                        {deleting === draftId
                          ? <Loader size={12} className="animate-spin"/>
                          : <Trash2 size={12}/>}
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Footer */}
      <div className="flex items-center px-4 py-3 border-t border-gray-100">
        <span className="text-xs text-slate-400">
          Showing <b className="text-gray-700">{drafts.length}</b> draft{drafts.length !== 1 ? "s" : ""}
        </span>
      </div>
    </div>
  );
}